import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BookOpen, Calculator, Plus, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { apiMessage, useQueryParams } from './shared';
import { RecipeDetailPanel } from './components/RecipeDetailPanel';
import { RecipeVersionEditor } from './components/RecipeVersionEditor';
import type { RecipeDetail, RecipeRow, RecipeVersion } from './components/recipeTypes';

interface CatalogItemOption {
  id: string;
  name: string;
}

/** Which version the editor opens on, and in which of its two modes. */
interface EditorState {
  recipeId: string;
  recipeName: string;
  /** The DRAFT being edited — the only version the API will accept edits to. */
  draft: RecipeVersion | null;
  /** A published version being copied into a fresh draft. */
  seed: RecipeVersion | null;
}

const BLANK_RECIPE = { name: '', description: '', catalogItemId: '' };

/**
 * §21 — recipes, their versions, and what a dish actually costs.
 *
 * A published version is never edited: changing a dish means a new version,
 * which is what keeps last month's COGS stable. Picking a recipe opens
 * `RecipeDetailPanel` (64:810) in place of the list — the bill of materials
 * costed at today's moving averages on the left, the cost summary, the item it
 * sells as, and the whole version history on the right.
 *
 * Authoring lives in `components/RecipeVersionEditor` and follows the same rule
 * the API enforces: a DRAFT is edited in place, while an ACTIVE or ARCHIVED
 * version can only be *copied* into a new draft. The screen never offers an edit
 * the server would refuse — the immutability is stated where the button would
 * otherwise be.
 */
export default function Recipes() {
  const { t } = useTranslation();
  const { can } = usePermission();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Bumped to remount the panel, which is how it refetches after a save. */
  const [detailKey, setDetailKey] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draftRecipe, setDraftRecipe] = useState(BLANK_RECIPE);

  const mayWrite = can('inventory.adjust');
  const mayReadCatalog = can('menu.view');

  const params = useQueryParams({ limit: 100, search });
  const list = useApi<unknown>('/recipes', params);
  const recipes = useMemo(() => rowsOf<RecipeRow>(list.data).rows, [list.data]);

  // Only offered when the actor may read the catalogue; a recipe with no
  // catalog item is still useful as a sub-recipe.
  const catalogApi = useApi<unknown>(createOpen && mayReadCatalog ? '/catalog' : null, {
    limit: 200,
    isActive: true,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const catalogItems = useMemo(
    () => rowsOf<CatalogItemOption>(catalogApi.data).rows,
    [catalogApi.data],
  );

  /**
   * A recipe is created bare, then its first version is authored as a DRAFT and
   * activated deliberately. `POST /recipes` can publish version 1 inline, but
   * that would mean building a BOM before the recipe exists to cost it against.
   */
  const createRecipe = async () => {
    const name = draftRecipe.name.trim();
    if (!name) return;
    setBusy('create');
    try {
      const { data } = await api.post<{ id: string; name: string }>('/recipes', {
        name,
        ...(draftRecipe.description.trim() ? { description: draftRecipe.description.trim() } : {}),
        ...(draftRecipe.catalogItemId ? { catalogItemId: draftRecipe.catalogItemId } : {}),
      });
      toast.success(t('platform.recipes.editor.recipeCreated'));
      setCreateOpen(false);
      setDraftRecipe(BLANK_RECIPE);
      list.reload();
      setEditor({ recipeId: data.id, recipeName: data.name, draft: null, seed: null });
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const openEditor = (recipe: RecipeDetail, draft: RecipeVersion | null, seed: RecipeVersion | null) =>
    setEditor({ recipeId: recipe.id, recipeName: recipe.name, draft, seed });

  return (
    <div className="space-y-4">
      {selectedId ? (
        <RecipeDetailPanel
          key={`${selectedId}:${detailKey}`}
          recipeId={selectedId}
          mayWrite={mayWrite}
          onBack={() => setSelectedId(null)}
          onEdit={openEditor}
          onChanged={() => list.reload()}
        />
      ) : (
        <>
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="flex items-center gap-2 text-xl font-semibold">
                <Icon as={BookOpen} size="lg" />
                {t('platform.recipes.title')}
              </h1>
              <p className="text-sm text-ink-muted">{t('platform.recipes.subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {mayWrite && (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Icon as={Plus} />
                  {t('platform.recipes.editor.newRecipe')}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={list.reload} disabled={list.loading}>
                <Icon as={RefreshCw} />
                {t('platform.approvals.refresh')}
              </Button>
            </div>
          </header>

          <Card>
            <CardContent className="pt-5">
              <Field label={t('common.search')}>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('platform.recipes.searchHint')}
                />
              </Field>
            </CardContent>
          </Card>

          {list.loading && !recipes.length ? (
            <LoadingState label={t('common.loading')} />
          ) : list.error ? (
            <ErrorState message={list.error} onRetry={list.reload} />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('platform.recipes.recipe')}</TH>
                    <TH>{t('platform.recipes.sellsAs')}</TH>
                    <TH>{t('platform.recipes.activeVersion')}</TH>
                    <TH className="text-end">{t('platform.recipes.versions')}</TH>
                    <TH className="text-end">{t('platform.actions')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {!recipes.length && (
                    <TableEmpty colSpan={5}>
                      <EmptyState
                        title={t('platform.recipes.empty')}
                        hint={t('platform.recipes.emptyHint')}
                      />
                    </TableEmpty>
                  )}

                  {recipes.map((recipe) => (
                    <TR key={recipe.id}>
                      <TD>
                        <button
                          type="button"
                          className="font-medium text-accent underline"
                          onClick={() => setSelectedId(recipe.id)}
                        >
                          {recipe.name}
                        </button>
                        {!recipe.isActive && (
                          <Badge tone="neutral" className="ms-2">
                            {t('platform.recipes.inactive')}
                          </Badge>
                        )}
                        {recipe.description && (
                          <div className="text-xs text-ink-muted">{recipe.description}</div>
                        )}
                      </TD>
                      <TD>{recipe.catalogItem?.name ?? '—'}</TD>
                      <TD>
                        {recipe.activeVersion ? (
                          <Badge tone={toneForStatus(recipe.activeVersion.status)}>
                            v{recipe.activeVersion.version}
                          </Badge>
                        ) : (
                          <span className="text-ink-muted">
                            {t('platform.recipes.noActiveVersion')}
                          </span>
                        )}
                      </TD>
                      <TD className="text-end tabular-nums">{recipe._count?.versions ?? '—'}</TD>
                      <TD>
                        <div className="flex justify-end gap-1.5">
                          {/* `explodeRecipe()` refuses a recipe with no
                              `activeVersionId` — there is no BOM to flatten, and
                              the detail panel says so where the cost would be. */}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!recipe.activeVersion}
                            title={
                              recipe.activeVersion
                                ? undefined
                                : t('platform.recipes.costNeedsActiveVersion')
                            }
                            onClick={() => setSelectedId(recipe.id)}
                          >
                            <Icon as={Calculator} />
                            {t('platform.recipes.cost')}
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </>
      )}

      {/* ------------------------------------------------------- new recipe */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('platform.recipes.editor.newRecipe')}
        description={t('platform.recipes.editor.newRecipeHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={busy === 'create' || !draftRecipe.name.trim()}
              onClick={() => void createRecipe()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label={t('platform.recipes.recipe')} required>
            <Input
              value={draftRecipe.name}
              onChange={(e) => setDraftRecipe({ ...draftRecipe, name: e.target.value })}
            />
          </Field>
          <Field label={t('platform.recipes.editor.description')}>
            <Textarea
              rows={2}
              value={draftRecipe.description}
              onChange={(e) => setDraftRecipe({ ...draftRecipe, description: e.target.value })}
            />
          </Field>
          {mayReadCatalog && (
            <Field
              label={t('platform.recipes.sellsAs')}
              hint={t('platform.recipes.editor.sellsAsHint')}
            >
              <Select
                value={draftRecipe.catalogItemId}
                onChange={(e) => setDraftRecipe({ ...draftRecipe, catalogItemId: e.target.value })}
              >
                <option value="">{t('platform.recipes.editor.noCatalogItem')}</option>
                {catalogItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
      </Dialog>

      {/* --------------------------------------------------- version authoring */}
      {editor && (
        <RecipeVersionEditor
          open
          recipeId={editor.recipeId}
          recipeName={editor.recipeName}
          draft={editor.draft}
          seed={editor.seed}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            setSelectedId(editor.recipeId);
            // A saved draft changes the version list and can change the active
            // version, so the panel is remounted onto the new answer.
            setDetailKey((key) => key + 1);
            list.reload();
          }}
        />
      )}
    </div>
  );
}
